import type { RefObject } from "react";
import { FiTrash2 } from "react-icons/fi";
import {
  MdBlock,
  MdReport,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import noImage from "@/assets/img/no-image.svg";
import { BOARD_CONFIG } from "@/config/browserBoard";
import type { Thread } from "@/entities/thread";
import { getImageUrl, getThreadTitle } from "@/entities/thread";
import { ThreadActionPopover } from "@/features/catalog/components/actions/ThreadActionPopover";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import { useNgStore } from "@/features/ng-filter/stores/ngStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDelMutation } from "@/shared/hooks/useDelMutation";
import { Button } from "@/shared/ui/form";
import { Modal } from "@/shared/ui/overlay";

interface Props {
  thread: Thread;
  isOpen: boolean;
  onClose: () => void;
  /**
   * PC版ポップオーバーの位置基準となる起点要素("…"ボタン)への参照。
   * PC版(`useIsDesktop()`)でのみ使用する。SP版はモーダルのまま変更しないため未使用。
   */
  anchorRef?: RefObject<HTMLElement | null>;
}

export const ThreadContextMenu: React.FunctionComponent<Props> = ({
  thread,
  isOpen,
  onClose,
  anchorRef,
}: Props) => {
  const isDesktop = useIsDesktop();
  const { hiddenThreadIds, hideThread, unhideThread, addNgImage, addNgTitle } =
    useNgStore();
  const removeFromHistory = useHistoryStore((state) => state.removeFromHistory);
  const { mutate: del } = useDelMutation();

  const handleDel = (): void => {
    if (confirm("スレッドをdelしますか？")) {
      del(thread.opPost.id);
    }
    onClose();
  };

  const handleHide = (): void => {
    hideThread(thread.id);
    onClose();
  };

  const handleUnhide = (): void => {
    unhideThread(thread.id);
    onClose();
  };

  const handleNgTitle = (): void => {
    const title = getThreadTitle(thread);
    addNgTitle(title);
    alert(`「${title.slice(0, 20)}...」をNGスレ文に追加しました`);
    onClose();
  };

  const handleNgImage = (): void => {
    const hash = thread.opPost.attachment?.ngHash;
    const result = hash
      ? addNgImage(hash, "スレ画像")
      : { success: false, message: "画像ハッシュが取得できませんでした" };
    alert(result.message);
    onClose();
  };

  const handleRemoveHistory = (): void => {
    removeFromHistory(thread.b ?? BOARD_CONFIG.boardKey, thread.id);
    alert("履歴から削除しました");
    onClose();
  };

  const imageUrl = getImageUrl(thread.opPost.attachment, false);
  const title = getThreadTitle(thread);

  const content = (
    <>
      {/* プレビュー */}
      <figure className="flex items-center gap-3 p-4 border-b border-border">
        <img
          src={imageUrl}
          alt=""
          className="w-16 h-16 rounded object-cover bg-muted"
          onError={(e: React.SyntheticEvent<HTMLImageElement>): void => {
            (e.target as HTMLImageElement).src = noImage;
          }}
        />
        <figcaption className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{title}</p>
          <p className="text-sm text-muted-foreground">
            スレッドID: {thread.id} / {thread.replyCount}レス
          </p>
        </figcaption>
      </figure>

      {/* アクション */}
      <menu className="m-0 list-none space-y-1 p-2">
        <li>
          <Button
            variant="menu"
            onClick={handleDel}
            icon={<MdReport aria-hidden="true" className="w-5 h-5" />}
          >
            del
          </Button>
        </li>
        {
          // スレが「非表示」機能によって非表示になっている場合のみ、「表示」ボタンを代わりに表示する。
          // つまり、NGワードなど別要因により非表示になっている場合は除く(とりあえずは)。
          // isThreadHiddenはNGワードを含む全ての要因を考慮するのでここでは使用できない。
          hiddenThreadIds.includes(thread.id) ? (
            <li>
              <Button
                variant="menu"
                onClick={handleUnhide}
                icon={<MdVisibility aria-hidden="true" className="w-5 h-5" />}
              >
                表示
              </Button>
            </li>
          ) : (
            <li>
              <Button
                variant="menu"
                onClick={handleHide}
                icon={
                  <MdVisibilityOff aria-hidden="true" className="w-5 h-5" />
                }
              >
                非表示
              </Button>
            </li>
          )
        }
        <li>
          <Button
            variant="menu"
            onClick={handleNgTitle}
            icon={<MdBlock aria-hidden="true" className="w-5 h-5" />}
          >
            NGスレ文追加
          </Button>
        </li>
        {thread.opPost.attachment?.ngHash && (
          <li>
            <Button
              variant="menu"
              onClick={handleNgImage}
              icon={<MdBlock aria-hidden="true" className="w-5 h-5" />}
            >
              画像をNG
            </Button>
          </li>
        )}
        <li>
          <Button
            variant="menu"
            onClick={handleRemoveHistory}
            icon={<FiTrash2 aria-hidden="true" className="w-5 h-5" />}
          >
            履歴から削除
          </Button>
        </li>
      </menu>
    </>
  );

  // PC版: 起点("…"ボタン)近傍にアンカー表示する小型ポップオーバー。
  // 画面全体を暗転させる全画面モーダルは使わない(Issue #37)。
  if (isDesktop && anchorRef) {
    return (
      <ThreadActionPopover
        isOpen={isOpen}
        onClose={onClose}
        anchorRef={anchorRef}
        ariaLabel="スレッド操作"
      >
        {content}
      </ThreadActionPopover>
    );
  }

  // SP版: 従来どおりボトムシートのモーダルのまま変更しない。
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="スレッド操作"
      position="bottom"
    >
      {content}
      <footer className="p-2 border-t border-border">
        <Button
          variant="ghost"
          onClick={onClose}
          className="w-full py-3 text-center"
        >
          キャンセル
        </Button>
      </footer>
    </Modal>
  );
};
